import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider as MUIThemeProvider, createTheme } from '@mui/material/styles';
import { lightTheme, darkTheme, ThemeType } from '../styles/theme';

interface ThemeContextType {
  isDark: boolean;
  toggleTheme: () => void;
  theme: ThemeType;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDark, setIsDark] = useState(false); // Light mode como padrão

  useEffect(() => {
    const stored = localStorage.getItem('hostly-dark-mode');
    if (stored !== null) {
      setIsDark(JSON.parse(stored));
    }
    // Se não há valor armazenado, mantém o padrão false (light mode)
  }, []);

  const toggleTheme = () => {
    const newDark = !isDark;
    setIsDark(newDark);
    localStorage.setItem('hostly-dark-mode', JSON.stringify(newDark));
  };

  const theme = isDark ? darkTheme : lightTheme;

  const muiTheme = createTheme({
    palette: {
      mode: isDark ? 'dark' : 'light',
      primary: {
        main: theme.colors.primary,
        contrastText: theme.colors.text.contrast,
      },
      secondary: {
        main: theme.colors.secondary,
      },
      background: {
        default: theme.colors.background,
        paper: theme.colors.card,
      },
      text: {
        primary: theme.colors.text.primary,
        secondary: theme.colors.text.secondary,
      },
    },
    shape: {
      borderRadius: parseInt(theme.radii.medium.replace('px', '')),
    },
  });

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme, theme }}>
      <MUIThemeProvider theme={muiTheme}>
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};
